import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import ArtListing from '../components/ArtListing';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ProfileImageContainer = styled.div`
  margin-right: 2rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 1.5rem;
  }
`;

const ProfileImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileImagePlaceholder = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #aaa;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 0.5rem 0;
`;

const BioText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  max-width: 800px;
`;

const Location = styled.div`
  font-size: 1rem;
  color: #666;
  margin-bottom: 1rem;
`;

const TabsContainer = styled.div`
  margin-bottom: 2rem;
  border-bottom: 1px solid #eee;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.$active ? '#000' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#000'};
  border: none;
  font-size: 1rem;
  cursor: pointer;
  margin-right: 1rem;
  
  &:hover {
    background: ${props => props.$active ? '#000' : '#f0f0f0'};
  }
`;

const ArtGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const ProfileImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  cursor: pointer;

  &:hover ${ProfileImageOverlay} {
    opacity: 1;
  }
`;

const PhotoButton = styled.button`
  background: white;
  border: none;
  padding: 8px 16px;
  margin: 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UserProfileScreen = ({ onUpdateUser }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Check if the profile is for the current logged-in user
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser && (!userId || userId === currentUser.uid)) {
        setIsCurrentUser(true);
        fetchUserData(currentUser.uid);
      } else if (userId) {
        setIsCurrentUser(false);
        fetchUserData(userId);
      } else {
        // No userId and no current user - redirect to sign in
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [userId, navigate]);

  const fetchUserData = async (id) => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', id));
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = { id: userDoc.id, ...userDoc.data() };
      setUser(userData);
      
      // Fetch artworks based on active tab
      await fetchArtworks(userData, activeTab);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setLoading(false);
    }
  };

  const fetchArtworks = async (userData, tab) => {
    try {
      const artworksRef = collection(db, 'artworks');
      let artworksQuery;
      
      if (tab === 'listings' && userData.isArtist) {
        // For listings tab, show artworks by this artist that are for sale
        artworksQuery = query(
          artworksRef,
          where('artist', '==', doc(db, 'users', userData.id)),
          where('isForSale', '==', true),
          orderBy('createdAt', 'desc')
        );
      } else if (tab === 'collection') {
        // For collection tab, show artworks owned by this user
        artworksQuery = query(
          artworksRef,
          where('owner', '==', doc(db, 'users', userData.id)),
          orderBy('createdAt', 'desc')
        );
      } else if (tab === 'all') {
        // For all artworks tab, show all artworks by this artist
        artworksQuery = query(
          artworksRef,
          where('artist', '==', doc(db, 'users', userData.id)),
          orderBy('createdAt', 'desc')
        );
      }
      
      if (artworksQuery) {
        const snapshot = await getDocs(artworksQuery);
        const artworksList = await Promise.all(snapshot.docs.map(async doc => {
          const artworkData = doc.data();
          let artistData;
          
          // If the artwork is created by the current user, use their data
          if (artworkData.artist.id === userData.id) {
            artistData = userData;
          } else {
            // Otherwise fetch the artist's data
            const artistDoc = await getDoc(artworkData.artist);
            artistData = artistDoc.exists() ? { id: artistDoc.id, ...artistDoc.data() } : null;
          }
          
          return {
            id: doc.id,
            ...artworkData,
            artistData
          };
        }));
        setArtworks(artworksList);
      } else {
        setArtworks([]);
      }
    } catch (error) {
      console.error('Error fetching artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (user) {
      fetchArtworks(user, tab);
    }
  };

  const handleArtworkClick = (artwork) => {
    navigate(`/artwork/${artwork.id}`);
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const storage = getStorage();
      const userId = auth.currentUser.uid;
      const photoRef = ref(storage, `profile_photos/${userId}`);

      // Upload the file
      await uploadBytes(photoRef, file);
      
      // Get the download URL
      const photoURL = await getDownloadURL(photoRef);

      // Update user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        profilePhoto: photoURL,
        lastModified: new Date()
      });

      // Update local state and parent state
      setUser(prev => ({
        ...prev,
        profilePhoto: photoURL
      }));
      if (onUpdateUser) {
        onUpdateUser({ profilePhoto: photoURL });
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    try {
      setUploading(true);
      const storage = getStorage();
      const userId = auth.currentUser.uid;
      const photoRef = ref(storage, `profile_photos/${userId}`);

      // Delete from storage
      try {
        await deleteObject(photoRef);
      } catch (error) {
        // Ignore if file doesn't exist
        console.log('No existing photo to delete');
      }

      // Update user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        profilePhoto: null,
        lastModified: new Date()
      });

      // Update local state and parent state
      setUser(prev => ({
        ...prev,
        profilePhoto: null
      }));
      if (onUpdateUser) {
        onUpdateUser({ profilePhoto: null });
      }
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <LoadingIndicator>Loading profile...</LoadingIndicator>;
  }

  if (!user) {
    return <EmptyMessage>User not found</EmptyMessage>;
  }

  const getInitials = () => {
    if (user.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
    }
    return '';
  };

  return (
    <Container>
      <ProfileSection>
        <ProfileImageContainer>
          <ProfileImageWrapper>
            {user.profilePhoto ? (
              <>
                <ProfileImage src={user.profilePhoto} alt={user.displayName} />
                {isCurrentUser && (
                  <ProfileImageOverlay>
                    <PhotoButton onClick={() => fileInputRef.current.click()}>
                      Change Photo
                    </PhotoButton>
                    <PhotoButton onClick={handleRemovePhoto}>
                      Remove
                    </PhotoButton>
                  </ProfileImageOverlay>
                )}
              </>
            ) : (
              <>
                <ProfileImagePlaceholder>{getInitials()}</ProfileImagePlaceholder>
                {isCurrentUser && (
                  <ProfileImageOverlay>
                    <PhotoButton onClick={() => fileInputRef.current.click()}>
                      Add Photo
                    </PhotoButton>
                  </ProfileImageOverlay>
                )}
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploading}
            />
          </ProfileImageWrapper>
        </ProfileImageContainer>
        
        <ProfileInfo>
          <UserName>{user.displayName}</UserName>
          
          <Location>
            {user.birthYear && <span>b. {user.birthYear}</span>}
            {user.birthYear && user.location && <span> • </span>}
            {user.location && <span>{user.location}</span>}
          </Location>
          
          <BioText>{user.bio || 'No bio available.'}</BioText>
          
          {user.instagramHandle && (
            <a href={`https://instagram.com/${user.instagramHandle}`} target="_blank" rel="noopener noreferrer" style={{ marginRight: '1rem' }}>
              Instagram
            </a>
          )}
          
          {user.websiteURL && (
            <a href={user.websiteURL} target="_blank" rel="noopener noreferrer">
              Website
            </a>
          )}
        </ProfileInfo>
      </ProfileSection>
      
      <TabsContainer>
        {user.isArtist && (
          <Tab 
            $active={activeTab === 'listings'} 
            onClick={() => handleTabChange('listings')}
          >
            Listings
          </Tab>
        )}
        
        {isCurrentUser && (
          <Tab 
            $active={activeTab === 'collection'} 
            onClick={() => handleTabChange('collection')}
          >
            Collection
          </Tab>
        )}
        
        {user.isArtist && (
          <Tab 
            $active={activeTab === 'all'} 
            onClick={() => handleTabChange('all')}
          >
            All Works
          </Tab>
        )}
      </TabsContainer>
      
      {artworks.length === 0 ? (
        <EmptyMessage>
          {activeTab === 'listings' ? 'No artworks listed for sale.' : 
           activeTab === 'collection' ? 'No artworks in collection.' : 
           'No artworks available.'}
        </EmptyMessage>
      ) : (
        <ArtGrid>
          {artworks.map(artwork => (
            <ArtListing 
              key={artwork.id} 
              artwork={artwork} 
              onClick={() => handleArtworkClick(artwork)} 
            />
          ))}
        </ArtGrid>
      )}
    </Container>
  );
};

export default UserProfileScreen;